import React, { useState } from 'react';
import { listInterviewEvaluations } from '../../graphql/queries';
import { API, Storage, graphqlOperation } from "aws-amplify";
import { updateInterviewEvaluation } from '../../graphql/mutations';
import Header from '../../components/header/Header';
import { RiseLoader } from "react-spinners";
import { 
  FiUpload, 
  FiMail, 
  FiVideo,
  FiCheckCircle,
  FiXCircle,
  FiSearch
} from "react-icons/fi";

const UpdateCandidate = () => {
  const [email, setEmail] = useState('');
  const [interviewEvaluations, setInterviewEvaluations] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [candidateId, setCandidateId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [checking, setChecking] = useState(false);

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  const fetchInterviewEvaluations = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setChecking(true);
    setError("");
    setSuccess("");
    let interviewEvaluationsItems = [];
    let currentNextToken = null;

    try {
      do {
        const interviewEvaluationsResponse = await API.graphql(
          graphqlOperation(listInterviewEvaluations, {
            filter: {
              candidateEmail: { eq: email },
            },
            limit: 100,
            nextToken: currentNextToken,
          })
        );

        interviewEvaluationsItems = [
          ...interviewEvaluationsItems,
          ...interviewEvaluationsResponse.data.listInterviewEvaluations.items,
        ];

        currentNextToken = interviewEvaluationsResponse.data.listInterviewEvaluations.nextToken;
      } while (currentNextToken);

      setInterviewEvaluations(interviewEvaluationsItems);
      
      if (interviewEvaluationsItems.length > 0 && interviewEvaluationsItems[0].id) {
        setCandidateId(interviewEvaluationsItems[0].id);
        setSuccess(`Found ${interviewEvaluationsItems.length} interview evaluation(s) for this candidate`);
      } else {
        setError('No interview evaluations found for this email address');
      }
    } catch (error) {
      console.error("Error fetching evaluations:", error);
      setError('Failed to fetch interview evaluations. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setError("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setVideoFile(file);
    setError("");
  };

  const update = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email.trim()) {
      setError('Email cannot be empty');
      return;
    }
    if (!videoFile) {
      setError('Please select a video file to upload');
      return;
    }
    if (!candidateId) {
      setError('Please check the email first');
      return;
    }

    setUploading(true);

    try {
      const yourS3BucketName = "getscreenedstoragebucketf24ed-devget";
      const folderPath = 'InterviewVideo/';
      
      let videoURL = null;
      if (videoFile) {
        const videoKey = await Storage.put(
          `${folderPath}${videoFile.name}`,
          videoFile,
          {
            contentType: "video/*",
          }
        );
        videoURL = `https://${yourS3BucketName}.s3.amazonaws.com/public/${videoKey.key}`;
      }

      const input = {
        id: candidateId,
        videoURL: videoURL,
      };

      const response = await API.graphql(
        graphqlOperation(updateInterviewEvaluation, { input })
      );

      setSuccess('Video uploaded and profile updated successfully!');
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setEmail('');
        setVideoFile(null);
        setInterviewEvaluations([]);
        setCandidateId('');
        setSuccess('');
      }, 3000);

    } catch (error) {
      console.error("Error updating profile:", error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <Header />
      
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-medium text-white tracking-tight mb-2">
            Update Interview Video
          </h1>
          <p className="text-slate-400 text-sm">
            Upload or update interview video for a candidate profile
          </p>
        </div>

        {/* Main Form Card */}
        <div className="bg-slate-800/40 rounded-xl p-8 space-y-6">
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 rounded-lg p-4 flex items-center gap-2">
              <FiXCircle size={20} />
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/50 text-green-400 rounded-lg p-4 flex items-center gap-2">
              <FiCheckCircle size={20} />
              {success}
            </div>
          )}

          {/* Email Input Section */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">
              <FiMail className="inline mr-2" />
              Candidate Email
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                onKeyPress={(e) => e.key === 'Enter' && fetchInterviewEvaluations()}
                placeholder="Enter candidate email..."
                className="flex-1 bg-slate-700 text-white border border-slate-600 rounded-lg py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                onClick={fetchInterviewEvaluations}
                disabled={checking || !email.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 py-2.5 rounded-lg transition-all flex items-center gap-2 font-medium"
              >
                <FiSearch size={18} />
                {checking ? 'Checking...' : 'Check'}
              </button>
            </div>
          </div>

          {/* Loading State */}
          {checking && (
            <div className="flex justify-center py-8">
              <RiseLoader color={"#3b82f6"} loading={checking} css={override} size={12} />
            </div>
          )}

          {/* Video Upload Section - Only show if evaluations found */}
          {interviewEvaluations.length > 0 && !checking && (
            <>
              <div className="pt-4 border-t border-slate-700">
                <label className="block text-slate-400 text-sm mb-2">
                  <FiVideo className="inline mr-2" />
                  Upload Video
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="videoFile"
                  />
                  <label
                    htmlFor="videoFile"
                    className="flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white border-2 border-dashed border-slate-600 rounded-lg py-8 cursor-pointer transition-all"
                  >
                    <FiUpload size={24} />
                    <span>
                      {videoFile ? videoFile.name : 'Click to select video file'}
                    </span>
                  </label>
                </div>
                {videoFile && (
                  <p className="text-slate-400 text-xs mt-2">
                    File size: {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>

              {/* Interview Evaluations Info */}
              <div className="bg-slate-700/40 rounded-lg p-4 border border-slate-600">
                <h3 className="text-white font-medium mb-2">Interview Evaluations Found</h3>
                <p className="text-slate-400 text-sm">
                  This candidate has {interviewEvaluations.length} interview evaluation{interviewEvaluations.length !== 1 ? 's' : ''}.
                  The video will be updated for the latest evaluation.
                </p>
              </div>

              {/* Update Button */}
              <button
                onClick={update}
                disabled={uploading || !videoFile}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <RiseLoader color={"#ffffff"} loading={uploading} size={8} />
                    <span>Uploading Video...</span>
                  </>
                ) : (
                  <>
                    <FiUpload size={18} />
                    <span>Update Profile Video</span>
                  </>
                )}
              </button>
            </>
          )}

          {/* Instructions */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2 text-sm">Instructions</h4>
            <ol className="text-slate-400 text-xs space-y-1 list-decimal list-inside">
              <li>Enter the candidate's email address</li>
              <li>Click "Check" to verify the candidate exists</li>
              <li>Select a video file to upload (MP4 format recommended)</li>
              <li>Click "Update Profile Video" to complete the upload</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateCandidate;
